import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, User, Clock, CheckCircle, XCircle, AlertCircle, ExternalLink, MessageSquare, Calendar, Target, Star, X, Award, Users } from 'lucide-react';
import type { CastingBrief } from '../lib/supabase';
import InfluencerAvatar from '../components/InfluencerAvatar';

const PLATFORM_ICONS = {
  Instagram: {
    icon: 'https://cdn.simpleicons.org/instagram',
    color: 'text-pink-500',
  },
  YouTube: {
    icon: 'https://cdn.simpleicons.org/youtube',
    color: 'text-red-500',
  },
  TikTok: {
    icon: 'https://cdn.simpleicons.org/tiktok',
    color: 'text-purple-500',
  },
  Twitch: {
    icon: 'https://cdn.simpleicons.org/twitch',
    color: 'text-violet-500',
  },
  Snapchat: {
    icon: 'https://cdn.simpleicons.org/snapchat',
    color: 'text-yellow-400',
  },
};

interface Influencer {
  id: string;
  fullName: string;
  username: string;
  bio?: string;
  category?: string;
  followers_count?: number;
  engagement_rate?: number;
  collabs_count?: number;
  verified?: boolean;
  profile_image_url?: string;
  profile_url?: string;
  platforms: {
    name: string;
    handle: string;
    audience: string;
  }[];
}

export default function PublicCastingView() {
  const { token } = useParams();
  const [casting, setCasting] = useState<CastingBrief | null>(null);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [decisions, setDecisions] = useState<Record<string, 'accepted' | 'maybe' | 'rejected'>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  console.log('PublicCastingView rendered with token:', token);

  useEffect(() => {
    if (token) {
      console.log('Loading casting data...');
      loadCastingData();
    } else {
      console.log('No token provided');
      setError('Token manquant dans l\'URL');
      setIsLoading(false);
    }
  }, [token]);

  const loadCastingData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Loading casting data for token:', token);

      // Import Supabase dynamically
      const { supabase } = await import('../lib/supabase');

      // Rechercher le casting par token
      const { data: castingData, error: castingError } = await supabase
        .from('casting_briefs')
        .select('*')
        .eq('public_token', token)
        .eq('public_link_active', true)
        .maybeSingle();

      if (castingError) {
        console.error('Error fetching casting:', castingError);
        setError('Erreur lors de la récupération du casting');
        setIsLoading(false);
        return;
      }

      if (!castingData) {
        console.log('No casting found for token:', token);
        setError('Ce lien de casting n\'existe pas ou n\'est plus actif.');
        setIsLoading(false);
        return;
      }

      // Normaliser les données
      const normalizedCasting = {
        ...castingData,
        startDate: castingData.start_date ? new Date(castingData.start_date) : new Date(),
        endDate: castingData.end_date ? new Date(castingData.end_date) : new Date(),
        deadline: castingData.deadline ? new Date(castingData.deadline) : undefined,
        createdAt: castingData.created_at ? new Date(castingData.created_at) : new Date(),
        updatedAt: castingData.updated_at ? new Date(castingData.updated_at) : new Date(),
        proposedInfluencers: castingData.proposed_influencers || [],
        selectedInfluencers: castingData.selected_influencers || [],
        activityLog: castingData.activity_log || [],
        publicToken: castingData.public_token,
        publicLinkActive: castingData.public_link_active,
        agencyId: castingData.agency_id
      } as CastingBrief;

      console.log('Casting data loaded:', normalizedCasting);

      // Vérifier si la deadline est dépassée
      if (normalizedCasting.deadline) {
        if (new Date() > normalizedCasting.deadline) {
          setError('La période de validation pour ce casting est terminée.');
          setIsLoading(false);
          return;
        }
      }

      setCasting(normalizedCasting);

      // Charger les détails des influenceurs
      if (normalizedCasting.proposedInfluencers && normalizedCasting.proposedInfluencers.length > 0) {
        const influencerIds = normalizedCasting.proposedInfluencers.map(inf => inf.influencerId);

        try {
          if (influencerIds.length > 0) {
            const { data: influencersData, error: influencersError } = await supabase
              .from('influencers')
              .select('*')
              .in('id', influencerIds);

            if (influencersError) {
              console.error('Error loading influencers:', influencersError);
            } else if (influencersData) {
              console.log('Influencers loaded:', influencersData.length, influencersData);
              console.log('Looking for influencer IDs:', influencerIds);
              setInfluencers(influencersData as Influencer[]);
            } else {
              console.log('No influencer data returned');
            }
          }
        } catch (influencersError) {
          console.error('Error loading influencers:', influencersError);
        }
      }

      // Initialiser les commentaires et décisions avec les valeurs existantes
      const initialComments: Record<string, string> = {};
      const initialDecisions: Record<string, 'accepted' | 'maybe' | 'rejected'> = {};

      if (normalizedCasting.proposedInfluencers) {
        normalizedCasting.proposedInfluencers.forEach(inf => {
          if (inf.brandComment) {
            initialComments[inf.influencerId] = inf.brandComment;
          }
          if (inf.brandStatus && inf.brandStatus !== 'pending') {
            initialDecisions[inf.influencerId] = inf.brandStatus;
          }
        });
      }

      setComments(initialComments);
      setDecisions(initialDecisions);

      console.log('Casting data loaded successfully');
    } catch (error) {
      console.error('Error loading casting data:', error);
      setError(`Une erreur est survenue lors du chargement des données: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitDecisions = async () => {
    if (!casting) return;

    try {
      setIsSubmitting(true);

      // Mettre à jour les décisions de la marque
      const updatedProposedInfluencers = casting.proposedInfluencers.map(inf => ({
        ...inf,
        brandStatus: decisions[inf.influencerId] || 'pending',
        brandComment: comments[inf.influencerId] || '',
        brandDecisionDate: decisions[inf.influencerId] ? new Date().toISOString() : inf.brandDecisionDate
      }));

      // Créer une entrée dans le journal d'activité
      const newActivityLog = [
        ...(casting.activityLog || []),
        {
          id: Date.now().toString(),
          action: 'Décisions marque reçues',
          user: 'Marque',
          timestamp: new Date().toISOString(),
          details: `${Object.keys(decisions).length} décision(s) soumise(s)`
        }
      ];

      // Mettre à jour Supabase
      const { supabase } = await import('../lib/supabase');

      console.log('Submitting decisions:', {
        castingId: casting.id,
        decisions,
        updatedProposedInfluencers
      });

      const { data, error: updateError } = await supabase
        .from('casting_briefs')
        .update({
          proposed_influencers: updatedProposedInfluencers,
          activity_log: newActivityLog,
          updated_at: new Date().toISOString()
        })
        .eq('id', casting.id!);

      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }

      console.log('Decisions submitted successfully:', data);
      setHasSubmitted(true);
    } catch (error) {
      console.error('Error submitting decisions:', error);
      setError('Une erreur est survenue lors de la soumission.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    console.log('Formatting date:', date);
    if (!date) return '';
    
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInfluencerDetails = (influencerId: string) => {
    return influencers.find(inf => inf.id === influencerId);
  };

  const formatNumber = (num: number | undefined) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const getDisplayName = (influencer: Influencer) => {
    return influencer.fullName || influencer.username || 'Unknown';
  };

  console.log('Render state:', { isLoading, error, casting: !!casting, hasSubmitted });

  const getDecisionColor = (decision: string) => {
    switch (decision) {
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'maybe':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getDecisionIcon = (decision: string) => {
    switch (decision) {
      case 'accepted':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'maybe':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !casting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien non valide</h1>
          <p className="text-gray-600 mb-6">
            {error || "Ce lien de casting n'existe pas ou n'est plus actif."}
          </p>
        </div>
      </div>
    );
  }

  if (hasSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Merci pour vos retours !</h1>
          <p className="text-gray-600 mb-6">
            Vos décisions ont été enregistrées avec succès. L'agence sera notifiée de vos choix.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600">
              Casting: <span className="font-medium text-gray-900">{casting.name}</span>
            </p>
            <p className="text-sm text-gray-600">
              Décisions soumises: {Object.keys(decisions).length} influenceur(s)
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-3">
              <img
                src="https://i.postimg.cc/jq4qbh4Y/logo-campaiyn.png"
                alt="Campaly"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{casting.name}</h1>
                <p className="text-sm text-gray-500">Validation casting • {casting.brand}</p>
              </div>
            </div>
            {casting.deadline && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>Deadline: {formatDate(casting.deadline)}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Brief description */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Brief de la campagne</h2>
          <p className="text-gray-700 mb-4">{casting.description}</p>
          
          {casting.requirements && casting.requirements.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Critères recherchés</h3>
              <ul className="space-y-1">
                {casting.requirements.map((req, index) => (
                  <li key={index} className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Influencers grid */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              Influenceurs proposés ({casting.proposedInfluencers?.length || 0})
            </h2>
            <div className="text-sm text-gray-600">
              Sélectionnez votre choix pour chaque influenceur
            </div>
          </div>

          {casting.proposedInfluencers && casting.proposedInfluencers.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {casting.proposedInfluencers.map((proposedInf, index) => {
                const influencer = getInfluencerDetails(proposedInf.influencerId);
                const currentDecision = decisions[proposedInf.influencerId];

                console.log('Rendering influencer:', {
                  proposedInf,
                  influencerId: proposedInf.influencerId,
                  foundInfluencer: influencer,
                  allInfluencers: influencers
                });

                return (
                  <div key={proposedInf.influencerId} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="relative flex-shrink-0">
                          <InfluencerAvatar
                            profileImageUrl={influencer?.profile_image_url}
                            name={proposedInf.fullName || influencer?.username}
                            size="lg"
                          />
                          {influencer?.verified && (
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                              <Award className="w-3.5 h-3.5 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">{proposedInf.fullName}</h3>
                              {influencer && (
                                <p className="text-sm text-gray-500">@{influencer.username}</p>
                              )}
                            </div>
                            {currentDecision && (
                              <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${getDecisionColor(currentDecision)}`}>
                                {getDecisionIcon(currentDecision)}
                                <span className="text-sm font-medium">
                                  {currentDecision === 'accepted' ? 'Accepté' :
                                   currentDecision === 'maybe' ? 'Peut-être' : 'Refusé'}
                                </span>
                              </div>
                            )}
                          </div>
                          {influencer?.category && (
                            <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 border border-blue-200">
                              {influencer.category}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Bio */}
                      {influencer?.bio && (
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                          {influencer.bio}
                        </p>
                      )}

                      {/* Stats - Only show if influencer data is loaded */}
                      {influencer && (influencer.followers_count || influencer.engagement_rate) && (
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="text-center p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="text-[10px] text-gray-500 mb-1.5 font-semibold uppercase tracking-wide">
                              Followers
                            </div>
                            <div className="font-bold text-base text-gray-900">
                              {formatNumber(influencer.followers_count)}
                            </div>
                          </div>
                          <div className="text-center p-3 bg-green-50 rounded-xl border border-green-100">
                            <div className="text-[10px] text-green-600 mb-1.5 font-semibold uppercase tracking-wide">
                              Engagement
                            </div>
                            <div className="font-bold text-base text-green-700">
                              {influencer.engagement_rate ? `${influencer.engagement_rate.toFixed(1)}%` : '—'}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Decision buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setDecisions(prev => ({ ...prev, [proposedInf.influencerId]: 'accepted' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                            currentDecision === 'accepted'
                              ? 'bg-green-100 text-green-700 border-green-300'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-green-300 hover:bg-green-50'
                          }`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Accepter
                        </button>
                        <button
                          onClick={() => setDecisions(prev => ({ ...prev, [proposedInf.influencerId]: 'maybe' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                            currentDecision === 'maybe'
                              ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-yellow-300 hover:bg-yellow-50'
                          }`}
                        >
                          <AlertCircle className="w-4 h-4" />
                          Peut-être
                        </button>
                        <button
                          onClick={() => setDecisions(prev => ({ ...prev, [proposedInf.influencerId]: 'rejected' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                            currentDecision === 'rejected'
                              ? 'bg-red-100 text-red-700 border-red-300'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-red-300 hover:bg-red-50'
                          }`}
                        >
                          <XCircle className="w-4 h-4" />
                          Refuser
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                      {/* Creative note */}
                      {proposedInf.creativeNote && (
                        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                          <h4 className="text-sm font-medium text-indigo-900 mb-2">💡 Idée créative</h4>
                          <p className="text-indigo-800">{proposedInf.creativeNote}</p>
                        </div>
                      )}

                      {/* View Profile Button */}
                      {influencer?.profile_url && (
                        <a
                          href={influencer.profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg mb-4"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Voir le profil Instagram
                        </a>
                      )}

                      {/* Platforms */}
                      {influencer && influencer.platforms && influencer.platforms.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Autres Plateformes</h4>
                          <div className="space-y-2">
                            {influencer.platforms.filter(p => p.name !== 'Instagram').map((platform, platformIndex) => (
                              <div key={platformIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-3">
                                  <img
                                    src={PLATFORM_ICONS[platform.name as keyof typeof PLATFORM_ICONS]?.icon || 'https://cdn.simpleicons.org/message'}
                                    alt={platform.name}
                                    className={`w-6 h-6 ${PLATFORM_ICONS[platform.name as keyof typeof PLATFORM_ICONS]?.color || 'text-gray-500'}`}
                                  />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{platform.name}</p>
                                    <p className="text-xs text-gray-500">@{platform.handle}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium text-gray-900">{platform.audience}</p>
                                  <a
                                    href={`https://${platform.name.toLowerCase()}.com/${platform.handle}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Voir le profil
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* References */}
                      {proposedInf.references && proposedInf.references.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Références</h4>
                          <div className="space-y-2">
                            {proposedInf.references.map((ref, refIndex) => (
                              <a
                                key={refIndex}
                                href={ref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                              >
                                <ExternalLink className="w-4 h-4 text-indigo-600" />
                                <span className="text-sm text-gray-700 truncate">Référence {refIndex + 1}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Comment section */}
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Commentaire (optionnel)
                        </label>
                        <textarea
                          value={comments[proposedInf.influencerId] || ''}
                          onChange={(e) => setComments(prev => ({ ...prev, [proposedInf.influencerId]: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Laissez un commentaire pour cet influenceur..."
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun influenceur proposé pour ce casting.</p>
            </div>
          )}

          {/* Submit section */}
          {casting.proposedInfluencers && casting.proposedInfluencers.length > 0 && (
            <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Finaliser vos choix</h3>
                  <p className="text-sm text-gray-600">
                    {Object.keys(decisions).length} décision(s) sur {casting.proposedInfluencers.length} influenceur(s)
                  </p>
                </div>
                <button
                  onClick={handleSubmitDecisions}
                  disabled={isSubmitting || Object.keys(decisions).length === 0}
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Envoyer mes décisions
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <img
                src="https://i.postimg.cc/jq4qbh4Y/logo-campaiyn.png"
                alt="Campaly"
                className="w-8 h-8 object-contain"
              />
              <span className="ml-2 text-lg font-semibold text-gray-900">Campaly</span>
            </div>
            <p className="text-sm text-gray-500">
              Ce lien de validation est privé et confidentiel.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}